import {
    MouseEvent as ReactMouseEvent,
    ReactElement,
    useCallback,
    useMemo,
    useState,
} from 'react'
import AddUser from './AddUser'
import {
    Box,
    Button,
    IconButton,
    TextField,
    Typography,
    InputAdornment,
    Tooltip,
} from '@mui/material'
import {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_RowSelectionState,
    MRT_SortingState,
} from 'material-react-table'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
    deleteUser,
    fetchUsers,
    resendUserInvitation,
    resetUserPassword,
} from '../../services/userServices'
import MRTTable from '../Common/Table'
import SearchIcon from '@mui/icons-material/Search'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import LockResetIcon from '@mui/icons-material/LockReset'
import { getRoleAuthId } from '../../utils/UserManagementUtils'
import { useAppSelector, useAppDispatch } from '../../redux/hook'
import { updateLoader } from '../../redux/slices/loaderSlice'
import theme from '../../theme/theme'
import {
    IformData,
    IOpenAddOrEditUser,
    User,
    UserApiResponse,
} from './interfaces'
import {
    defaultAddUserFormData,
    MODE,
    UserPermissions,
    USER_ROLES,
} from '../../constants/constants'
import { formatDateTimeWithUserPreferences } from '../../utils/DateAndTimeUtils'
import { useTranslation } from 'react-i18next'
import APIToken from '../Layout/Navbar/APIToken'
import ConfirmationDialog from '../Common/ConfirmationDialog'
import { useRoutePermission } from '../../hooks/useRoutePermission'
import { usePermissions } from '../../hooks/usePermissions'

const fetchSize = 30
const actionColumnSize = 35
const lastActionColumnSize = 70

const actionCellSx = {
    backgroundColor: '#E9F6FF',
    borderBottom: '3px solid #FFFFFF',
    borderTop: '3px solid #FFFFFF',
    fontSize: '14px',
    padding: '0px',
    textAlign: 'center',
}

const actionIconButtonSx = {
    backgroundColor: 'transparent',
    height: 28,
    padding: '2px',
    width: 28,
    '&:hover': {
        backgroundColor: 'transparent',
    },
}

const displayColumnDefOptions = {
    'mrt-row-select': {
        header: '',
        size: actionColumnSize,
        minSize: actionColumnSize,
        grow: false,
        maxSize: actionColumnSize,
        muiTableBodyCellProps: {
            sx: {
                ...actionCellSx,
                '& .MuiCheckbox-root': {
                    padding: '2px',
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                padding: '0px',
                textAlign: 'center',
            },
        },
    },
}

const UserManagement = (): ReactElement => {
    const { t } = useTranslation()
    const [tableKey, setTableKey] = useState(0)
    const org = useAppSelector((state) => state.org.org)
    const dispatch = useAppDispatch()
    const roles = useAppSelector((state) => state.roles.roles)
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )
    const { hasPermission } = usePermissions()
    const hasWriteAccess = hasPermission(UserPermissions.USER_ADMIN)
    const isSuperAdmin = hasPermission(UserPermissions.ORG_ADMIN)
    //checking the permissions, if user doesn't have permission. redirecting to home
    useRoutePermission([
        UserPermissions.USER_ADMIN,
        UserPermissions.SYSTEM_READER,
    ])
    const [openAddOrEditUser, setOpenAddOrEditUser] =
        useState<IOpenAddOrEditUser>({
            mode: MODE.ADD,
            open: false,
            formData: defaultAddUserFormData,
        })
    const [openApiKeyPopUp, setOpenApiKeyPopUp] = useState<boolean>(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [selectedRowData, setSelectedRowData] = useState<User>()
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )
    const [globalFilter, setGlobalFilter] = useState<string>()
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

    const handleAddOrEditUser = useCallback((refreshTable: boolean) => {
        // Update the key value to force re-render of OrgTable component
        if (refreshTable) {
            setTableKey((prevKey) => prevKey + 1)
            setRowSelection({})
        }
        setOpenAddOrEditUser({
            mode: MODE.ADD,
            open: false,
            formData: defaultAddUserFormData,
        })
    }, [])

    const handleOpen = useCallback((mode: MODE, data: IformData) => {
        setOpenAddOrEditUser({
            mode,
            open: true,
            formData: data,
        })
    }, [])

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<UserApiResponse>({
            queryKey: [
                'user-management-table-data',
                columnFilters, // refetch when columnFilters changes
                globalFilter, // refetch when globalFilter changes
                sorting, // refetch when sorting changes
                org,
                tableKey,
            ],
            queryFn: async ({ pageParam }) => {
                let filterBy = sorting[0]?.id
                let sortOrder = ''
                if (filterBy) {
                    sortOrder = sorting[0]?.desc ? 'DESC' : 'ASC'
                }
                if (filterBy === 'Created At') {
                    filterBy = 'CREATED'
                }
                if (
                    org?.id &&
                    hasPermission([
                        UserPermissions.USER_ADMIN,
                        UserPermissions.SYSTEM_READER,
                    ])
                ) {
                    const response = await fetchUsers({
                        pageNumber: (pageParam as number) + 1,
                        size: fetchSize,
                        searchText: globalFilter?.trim() ?? '',
                        filterBy: filterBy ? filterBy.toUpperCase() : '',
                        sortOrder,
                        orgId: org?.id,
                    })
                    return response as UserApiResponse
                } else {
                    return null as unknown as UserApiResponse
                }
            },
            initialPageParam: 0,
            getNextPageParam: (lastPage, pages) => {
                // Check if we got any data
                if (!lastPage?.usersModels?.length) {
                    return undefined
                }

                // Calculate if we've received all data
                const totalFetched = pages.reduce(
                    (acc, page) => acc + (page?.usersModels?.length || 0),
                    0,
                )
                if (totalFetched >= (lastPage?.meta?.totalRowCount || 0)) {
                    return undefined
                }

                return pages.length
            },
            refetchOnWindowFocus: false,
        })
    const flatData = useMemo(
        () =>
            data?.pages?.flatMap((page) =>
                page?.usersModels ? page?.usersModels : [],
            ) ?? [],
        [data],
    )

    const totalDBRowCount = useMemo(
        () => data?.pages?.[0]?.meta?.totalRowCount ?? 0,
        [data],
    )
    const totalDBRowCountWithoutFilters = useMemo(
        () => data?.pages?.[0]?.meta?.totalCount ?? 0,
        [data],
    )

    const openAddUser = useCallback(() => {
        handleOpen(MODE.ADD, {
            ...defaultAddUserFormData,
            totalUsers: totalDBRowCountWithoutFilters,
        })
    }, [totalDBRowCountWithoutFilters, handleOpen])
    const openEditUser = useCallback(
        (rowData: User) => {
            handleOpen(MODE.EDIT, {
                email: rowData.email,
                role: rowData.role,
                id: rowData.id,
                orgId: rowData.orgId,
                totalUsers: totalDBRowCountWithoutFilters,
                units: rowData.preferences.units,
                localization: rowData?.preferences?.localization,
            })
        },
        [totalDBRowCountWithoutFilters, handleOpen],
    )

    const openUserDeleteConfirmation = useCallback(() => {
        setOpenDeleteDialog(true)
    }, [])
    const closeUserDeleteConfirmation = useCallback(() => {
        setOpenDeleteDialog(false)
    }, [])

    const deleteUsersHandler = useCallback(async () => {
        const keys = Object.keys(rowSelection)
        const usersToDelete: number[] = keys.map(
            (key) => flatData[Number(key)].id,
        )
        await deleteUser(usersToDelete, org!.id)

        // Update the key value to force re-render of OrgTable component
        setRowSelection({})
        setTableKey((prevKey) => prevKey + 1)
        setOpenDeleteDialog(false)
    }, [flatData, org, rowSelection])

    const apiKeyHandler = useCallback((rowData: User) => {
        setOpenApiKeyPopUp(true)
        setSelectedRowData(rowData)
    }, [])

    const resendInvitationHandler = useCallback(
        async (rowData: User) => {
            dispatch(updateLoader(true))
            try {
                await resendUserInvitation(
                    {
                        id: rowData.id,
                        roleAuth0Id: getRoleAuthId(roles, rowData.role),
                    },
                    org?.id || '',
                )
            } finally {
                dispatch(updateLoader(false))
            }
        },
        [roles, org?.id, dispatch],
    )

    const resetPasswordHandler = useCallback(
        async (rowData: User) => {
            dispatch(updateLoader(true))
            try {
                await resetUserPassword(
                    {
                        email: rowData.email,
                        userId: rowData.id,
                    },
                    org?.id || '',
                )
            } finally {
                dispatch(updateLoader(false))
            }
        },
        [org?.id, dispatch],
    )

    const disableDeleteButton = useMemo(
        () => Object.keys(rowSelection).length < 1,
        [rowSelection],
    )
    const columns: MRT_ColumnDef<User>[] = useMemo(
        () => [
            {
                header: '',
                accessorKey: 'editid',
                id: 'editid',
                Cell: ({ row }) => (
                    <IconButton
                        id={`row-${row.id}-edit-button`}
                        onClick={(event) => {
                            event.stopPropagation()
                            openEditUser(row.original)
                        }}
                        aria-controls="simple-menu-preview"
                        aria-haspopup="true"
                        aria-label={
                            hasWriteAccess ? t('editUser') : t('viewUser')
                        }
                        sx={actionIconButtonSx}
                        title={hasWriteAccess ? t('editUser') : t('viewUser')}
                    >
                        {hasWriteAccess ? (
                            <img
                                src="/assets/images/icons/edit.svg"
                                alt={t('editUser')}
                            />
                        ) : (
                            <img
                                src="/assets/images/icons/preview.svg"
                                alt={t('viewUser')}
                            />
                        )}
                    </IconButton>
                ),
                size: actionColumnSize,
                minSize: actionColumnSize,
                grow: false,
                maxSize: actionColumnSize,
                enableResizing: false,
                enableSorting: false,
                enableColumnFilter: false,
                muiTableBodyCellProps: {
                    role: 'gridcell',
                    sx: actionCellSx,
                    onClick: (event: ReactMouseEvent) =>
                        event.stopPropagation(),
                },
            },
            ...(isSuperAdmin
                ? [
                      {
                          header: '',
                          accessorKey: 'apiKeyId',
                          id: 'apiKeyId',
                          Cell: ({
                              row,
                          }: {
                              row: { id: string; original: User }
                          }) => {
                              const isApiKeyDisabled =
                                  row.original.status === 'Invited'

                              return (
                                  <IconButton
                                      id={`row-${row.id}-api-key-button`}
                                      onClick={(event) => {
                                          event.stopPropagation()
                                          apiKeyHandler(row.original)
                                      }}
                                      disabled={isApiKeyDisabled}
                                      aria-label={t('generateAPIToken')}
                                      aria-disabled={isApiKeyDisabled}
                                      sx={{
                                          ...actionIconButtonSx,
                                          filter: isApiKeyDisabled
                                              ? 'grayscale(100%)'
                                              : 'none',
                                          opacity: isApiKeyDisabled ? 0 : 1,
                                      }}
                                      title={t('generateAPIToken')}
                                  >
                                      <img
                                          src="/assets/images/icons/access_key.svg"
                                          alt={t('generateAPIToken')}
                                      />
                                  </IconButton>
                              )
                          },
                          size: actionColumnSize,
                          minSize: actionColumnSize,
                          grow: false,
                          maxSize: actionColumnSize,
                          enableResizing: false,
                          enableSorting: false,
                          enableColumnFilter: false,
                          muiTableBodyCellProps: {
                              role: 'gridcell',
                              sx: actionCellSx,
                              onClick: (event: ReactMouseEvent) =>
                                  event.stopPropagation(),
                          },
                      } as MRT_ColumnDef<User>,
                  ]
                : []),
            {
                accessorKey: 'email',
                header: t('userEmail'),
            },
            {
                accessorFn: (row) =>
                    row.role[0].toUpperCase() + row.role.substring(1),
                header: t('role'),
            },
            {
                accessorKey: 'status',
                header: t('status'),
                enableFilterMatchHighlighting: false,
            },
            {
                accessorFn: (row) => {
                    if (row.createdAt) {
                        return formatDateTimeWithUserPreferences(
                            row.createdAt,
                            userLocalization,
                        )
                    } else {
                        return ''
                    }
                },
                header: t('createdAt'),
                enableFilterMatchHighlighting: false,
            },
            {
                accessorKey: 'name',
                header: t('invitedBy'),
            },
            ...(hasWriteAccess
                ? [
                      {
                          header: '',
                          accessorKey: 'userActions',
                          id: 'userActions',
                          Cell: ({
                              row,
                          }: {
                              row: { id: string; original: User }
                          }) => {
                              const { status, role } = row.original
                              const isResend = status === 'Invited'
                              const isReset = status === 'Active'
                              // Device Manager users may only be resent an
                              // invitation / reset by a super admin; hide the
                              // action for everyone else.
                              const isRestrictedRole =
                                  role === USER_ROLES.DEVICE_MANAGER &&
                                  !isSuperAdmin
                              if ((!isResend && !isReset) || isRestrictedRole) {
                                  return null
                              }
                              return (
                                  <Tooltip
                                      title={
                                          isResend
                                              ? t('resendInvitation')
                                              : t('sendResetPasswordEmail')
                                      }
                                      placement="top"
                                  >
                                      <IconButton
                                          id={`row-${row.id}-${
                                              isResend
                                                  ? 'resend-invitation'
                                                  : 'reset-password'
                                          }-button`}
                                          onClick={(event) => {
                                              event.stopPropagation()
                                              if (isResend) {
                                                  resendInvitationHandler(
                                                      row.original,
                                                  )
                                              } else {
                                                  resetPasswordHandler(
                                                      row.original,
                                                  )
                                              }
                                          }}
                                          aria-label={
                                              isResend
                                                  ? t('resendInvitation')
                                                  : t('sendResetPasswordEmail')
                                          }
                                          sx={actionIconButtonSx}
                                      >
                                          {isResend ? (
                                              <MailOutlineIcon
                                                  fontSize="small"
                                                  sx={{
                                                      color: theme.palette
                                                          .primary.main,
                                                  }}
                                              />
                                          ) : (
                                              <LockResetIcon
                                                  fontSize="small"
                                                  sx={{
                                                      color: theme.palette
                                                          .primary.main,
                                                  }}
                                              />
                                          )}
                                      </IconButton>
                                  </Tooltip>
                              )
                          },
                          size: lastActionColumnSize,
                          minSize: lastActionColumnSize,
                          grow: false,
                          maxSize: lastActionColumnSize,
                          enableResizing: false,
                          enableSorting: false,
                          enableColumnFilter: false,
                          muiTableBodyCellProps: {
                              role: 'gridcell',
                              sx: actionCellSx,
                              onClick: (event: ReactMouseEvent) =>
                                  event.stopPropagation(),
                          },
                      } as MRT_ColumnDef<User>,
                  ]
                : []),
        ],
        [
            apiKeyHandler,
            hasWriteAccess,
            isSuperAdmin,
            openEditUser,
            resendInvitationHandler,
            resetPasswordHandler,
            t,
            userLocalization,
        ],
    )

    return (
        <Box
            sx={{
                margin: '30px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '20px',
                maxHeight: `calc(100vh - 199px)`,
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" component="h2">
                        {t('users')}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            background: '#E3F4FF',
                            borderRadius: '25px',
                            marginRight: '10px',
                        }}
                    >
                        <TextField
                            id="user-list-search"
                            aria-label={t('searchUsers')}
                            sx={{
                                padding: 0,
                                '& fieldset': { border: 'none' },
                            }}
                            placeholder={t('search')}
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="table-search-bar"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            sx={{
                                                '&:hover ': {
                                                    background: 'none',
                                                    cursor: 'auto',
                                                },
                                            }}
                                        >
                                            <SearchIcon
                                                sx={{ color: 'primary' }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: {
                                    height: 36,
                                    paddingLeft: '5px',
                                },
                            }}
                        />
                    </Box>
                    {openApiKeyPopUp && (
                        <APIToken
                            handleClose={() => setOpenApiKeyPopUp(false)}
                            userId={selectedRowData?.id}
                            userName={selectedRowData?.email}
                        />
                    )}
                    {hasWriteAccess ? (
                        <>
                            <Box
                                sx={{
                                    cursor: disableDeleteButton
                                        ? 'not-allowed'
                                        : 'default',
                                }}
                            >
                                <Button
                                    onClick={openUserDeleteConfirmation}
                                    disabled={disableDeleteButton}
                                    aria-label={t('deleteSelectedUser')}
                                    aria-disabled={disableDeleteButton}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '5px',
                                        filter: disableDeleteButton
                                            ? 'grayscale(100%)'
                                            : 'none',
                                        opacity: disableDeleteButton ? 0.5 : 1,
                                    }}
                                    id="delete-user-button"
                                >
                                    <img
                                        src={`/assets/images/icons/delete-icon.svg`}
                                        alt={t('deleteUser')}
                                    />
                                </Button>
                            </Box>
                            <Button
                                onClick={openAddUser}
                                sx={{
                                    minWidth: '25px',
                                    maxWidth: '30px',
                                    marginLeft: '15px',
                                    minHeight: '25px',
                                    maxHeight: '30px',
                                    marginRight: '10px',
                                }}
                                id="add-user-button"
                                aria-label={t('addUser')}
                                aria-disabled={disableDeleteButton}
                            >
                                <img
                                    src={`/assets/images/icons/add-icon.svg`}
                                    alt={t('addUser')}
                                />
                            </Button>
                        </>
                    ) : null}
                    {openAddOrEditUser.open && (
                        <AddUser
                            openAddOrEditUser={openAddOrEditUser}
                            handleAddOrEditUser={handleAddOrEditUser}
                            readOnly={!hasWriteAccess}
                        />
                    )}
                </Box>
            </Box>
            <MRTTable<User, UserApiResponse>
                key={JSON.stringify(userLocalization) || 'default'}
                columns={columns}
                sorting={sorting}
                globalFilter={globalFilter}
                columnFilters={columnFilters}
                setRowSelection={setRowSelection}
                rowSelection={rowSelection}
                setColumnFilters={setColumnFilters}
                setGlobalFilter={setGlobalFilter}
                setSorting={setSorting}
                data={flatData}
                totalDBRowCount={totalDBRowCount}
                fetchNextPage={fetchNextPage}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                enableBottomToolbar={true}
                isCurvedBottomToolbar={true}
                enableRowSelection={hasWriteAccess}
                displayColumnDefOptions={displayColumnDefOptions}
                initialState={{ columnPinning: { right: ['userActions'] } }}
                maxHeight="calc(100vh - 348px)"
            />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={deleteUsersHandler}
                handleClose={closeUserDeleteConfirmation}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('deleteUsersConfirmation')}
            />
        </Box>
    )
}

export default UserManagement
